import { memo, useState, useCallback, useEffect } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography, TextField, Drawer, Box, Button } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
// Local files
import DrawerSkeleton from 'components/Common/BaseDrawerSkeleton/BaseDrawerSkeleton';
import BaseEventsList from 'components/Analyses/Common/BaseEventsList/BaseEventsList';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import useCustomSelector from 'hooks/useCustomSelector';
import usePatientAnalyses from 'hooks/usePatientAnalyses';
import useEvents from 'hooks/useEvents';
import { eventsSelectors } from "features/events";
import { patientAnalysesSelectors } from 'features/patientAnalyses';

const PatientAnalysisDrawer = () => {
  const mediaDesktop = useMediaQuery(theme => theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const { patient_analysis_id } = useParams();
  const { getPatientAnalysis, clearLocalActivePatientAnalysis } = usePatientAnalyses();
  const { getPatientAnalysesEvents, clearLocalAnalysesEvents } = useEvents();
  const { data, pagination } = useCustomSelector(eventsSelectors.getAnalysesEventsData);
  const eventsFetching = useCustomSelector(eventsSelectors.analysesEventsIsFetching);
  const patientAnalysisFetching = useCustomSelector(patientAnalysesSelectors.patientAnalysisIsFetching);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [firstTimeEventsOpened, setFirstTimeEventsOpened] = useState(true);

  const fetchPatientAnalysis = useCallback((id) => {
    setOpen(true);
    getPatientAnalysis({ id })
    .then(({ payload, error }) => {
      if (!error) {
        const { data: { patient_analysis } } = payload;
        handleSetData(patient_analysis);
      }
    })
  }, [getPatientAnalysis]);

  const handleSetData = (patient_analysis) => {
    setResult(patient_analysis?.result);
  };

  const handleExiting = () => {
    setResult('');
    setExpanded(false);
    setFirstTimeEventsOpened(true);
    navigate(`/flow/fl71p`);
    clearLocalActivePatientAnalysis();
    clearLocalAnalysesEvents();
  };

  const handleClose = () => setOpen(false);

  const fetchPatientAnalysesEvents = useCallback((offset, patient_analysis_id) => {
    getPatientAnalysesEvents({ offset, patient_analysis_id })
  }, [ getPatientAnalysesEvents]);

  const handleChangeExpand = (_, isExpanded) => {
    setExpanded(isExpanded);
    if (firstTimeEventsOpened) {
      fetchPatientAnalysesEvents(0, patient_analysis_id);
      setFirstTimeEventsOpened(false);
    }
  }

  useEffect(() => {
    if (!!patient_analysis_id) fetchPatientAnalysis(patient_analysis_id);
  }, [patient_analysis_id, fetchPatientAnalysis]);

  return (
    <Drawer
      anchor={!mediaDesktop ? 'bottom' : 'right'}
      open={open}
      SlideProps={{ onExited: handleExiting }}
      onClose={handleClose}
      ModalProps={{ sx: { zIndex: 1300 } }}
      PaperProps={{
        sx: {
          height: '100%',
          width: { xs: '100%', md: '600px'},
          backgroundColor: 'background.default'
        }
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', p: '8px 8px 0' }}>
          <Button color='inherit' sx={{ fontWeight: 700 }} onClick={handleClose}>Close</Button>
          <div style={{ flexGrow: 1 }}/>
        </Box>
        <Box sx={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center'}}>
            Request log
        </Box>
      </Box>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: '16px 100px 32px' } }}>
      { patientAnalysisFetching ? <DrawerSkeleton /> :
        <>
          <Typography variant='body1' sx={{ fontWeight: 600, textAlign: 'center', mb: '10px' }}>Job Id: {patient_analysis_id}</Typography>
          <TextField
            fullWidth
            margin='normal'
            label='Result'
            multiline
            minRows={5}
            maxRows={8}
            disabled
            value={result ?? ''}
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: "#073842",
              }
            }}
          />
          <BaseEventsList
            data={data}
            pagination={pagination}
            analysisId={patient_analysis_id}
            eventsFetching={eventsFetching}
            fetchEvents={fetchPatientAnalysesEvents}
            expanded={expanded}
            handleChangeExpand={handleChangeExpand}
          />
        </>}
      </Box>
    </Drawer>
  );
};

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <PatientAnalysisDrawer {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);